"use client"

import clsx from "clsx"
import Link from "next/link"
import React, { useContext } from "react"

import RippleLogoDark from "../../../../public/assets/ripple-logo-dark.svg"
import RippleLogoLight from "../../../../public/assets/ripple-logo-light.svg"
import Arrow from "../../../../public/icons/arrow-up.svg"
import FBIcon from "../../../../public/icons/footer/footer_icon_FB.svg"
import LinkedInIcon from "../../../../public/icons/footer/footer_icon_LinkedIn.svg"
import TwitterIcon from "../../../../public/icons/footer/footer_icon_Twitter.svg"
import YoutubeIcon from "../../../../public/icons/footer/footer_icon_Youtube.svg"
import LinkOut from "../../../../public/icons/link-out.svg"
import PageThemeContext from "../page-theme/PageThemeContext"
import PageGrid from "../PageGrid"

const scrollToTop = (event) => {
  event.preventDefault()
  window.scrollTo({
    top: 0,
    behavior: "smooth",
  })
}

interface ILink {
  to: string
  text: string
  target?: string
  clickhandler?: () => void
  outbound?: boolean
}

interface MenuSection {
  title: string
  links: ILink[]
}

const menuValues: MenuSection[][] = [
  [
    {
      title: "Solutions",
      links: [
        {
          to: "/solutions/cross-border-payments/",
          text: "Payments",
        },
        {
          to: "/solutions/digital-asset-custody/",
          text: "Custody",
        },
        {
          to: "/solutions/stablecoin/",
          text: "Stablecoin",
        },
      ],
    },
    {
      title: "Use Cases",
      links: [
        {
          to: "/use-case/banks/",
          text: "Banks",
        },
        {
          to: "/use-case/fintechs/",
          text: "Fintechs",
        },
        {
          to: "/use-case/psp/",
          text: "Payment Service Providers",
        },
        {
          to: "/use-case/crypto-businesses/",
          text: "Crypto Businesses",
        },
      ],
    },
    {
      title: "Customers",
      links: [
        {
          to: "/customers/",
          text: "Customers Overview",
        },
        {
          to: "/customer-case-study/",
          text: "Customer Case Studies",
        },
        {
          to: "https://home.ripple.com/",
          text: "Ripple Customer Login",
          target: "_blank",
        },
      ],
    },
  ],
  [
    {
      title: "Company",
      links: [
        {
          to: "/company/",
          text: "About Us",
        },
        {
          to: "/company/leadership/",
          text: "Leadership",
        },
        {
          to: "/public-policy/",
          text: "Public Policy",
        },
        {
          to: "/investments/",
          text: "Investments",
        },
        {
          to: "/careers/",
          text: "Careers",
        },
        {
          to: "/impact/",
          text: "Ripple Impact",
        },
        {
          to: "/company/sbi-ripple-asia/",
          text: "SBI Ripple Asia",
        },
      ],
    },
    {
      title: "Learning & Education",
      links: [
        {
          to: "/insights/",
          text: "Blog",
        },
        {
          to: "/content-library/",
          text: "Content Library",
        },
        {
          to: "/events/",
          text: "Events",
        },
        {
          to: "/xrp/",
          text: "About XRP",
        },
        {
          to: "/faq/",
          text: "FAQs",
        },
      ],
    },
    {
      title: "Resources",
      links: [
        {
          to: "/contact/",
          text: "Get Support",
        },
        {
          to: "/press-releases/",
          text: "Press Center",
        },
        {
          to: "/legal/compliance/",
          text: "Compliance",
        },
        {
          to: "",
          text: "Your Privacy Choices",
          clickhandler: () =>
            typeof window !== "undefined" &&
            window?.Osano.cm.showDrawer("osano-cm-dom-info-dialog-open"),
        },
        {
          to: "https://docs.ripple.com/",
          text: "Ripple Product Docs",
          outbound: true,
        },
        {
          to: "https://xrpl.org/docs",
          text: "XRP Ledger Docs",
          outbound: true,
        },
        {
          to: "/security",
          text: "Security",
        },
        {
          to: "/suppliers/",
          text: "Supplier Resources Center",
        },
      ],
    },
  ],
]

interface FooterLinkProps extends ILink {
  className?: string
}

const FooterLink: React.FC<FooterLinkProps> = ({
  to,
  text,
  className,
  clickhandler,
  target,
  outbound,
}) => {
  if (to && text) {
    return (
      <li>
        <Link
          key={text}
          href={to}
          // only add target attribute if we pass it, one of the links in the footer needed to open in a new tab
          // now any link can set it's target attribute
          {...(target && { target })}
          className={clsx("block", className)}
          rel="noreferrer"
        >
          {text}
          {outbound && (
            <LinkOut
              alt="Link Out Icon"
              className="ml-2 inline stroke-blue-50"
            />
          )}
        </Link>
      </li>
    )
  }
  if (to === "" && clickhandler) {
    return (
      <li>
        <button
          type="button"
          key={text}
          className={clsx(className)}
          onClick={() => {
            clickhandler()
          }}
        >
          {text}
        </button>
      </li>
    )
  }
  return null
}

const Footer: React.FC = () => {
  const items = menuValues

  const pageThemeContext = useContext(PageThemeContext)

  const themeColorClassOne = pageThemeContext.pageTheme.dark
    ? "text-gray-2.5 hover:text-gray-40"
    : "text-gray-90 hover:text-gray-70"

  const themeColorClassTwo = pageThemeContext.pageTheme.dark
    ? "text-gray-40"
    : "text-gray-70"

  const themeColorClassThree = pageThemeContext.pageTheme.dark
    ? "hover:fill-gray-15 fill-gray-40"
    : "hover:fill-gray-85 fill-gray-70"

  return (
    <footer
      id="site-footer"
      role="contentinfo"
      className={clsx(
        "header-footer-group py-28",
        pageThemeContext.pageTheme.bgColorClassFooter,
      )}
    >
      <PageGrid className="flex-wrap">
        <div className="p-4 col-span-full lg:col-span-3">
          <div className="mb-8 md:mb-10">
            {pageThemeContext.pageTheme.dark ? (
              <RippleLogoLight
                width="120"
                height="33"
                alt="Ripple Logo"
                className="mb-12"
              />
            ) : (
              <RippleLogoDark
                width="120"
                height="33"
                alt="Ripple Logo"
                className="mb-12"
              />
            )}
            <button
              type="button"
              onClick={scrollToTop}
              className={clsx(
                "flex caption2 to-the-top cursor-pointer",
                themeColorClassOne,
              )}
            >
              Back to top
              <Arrow alt="Arrow Icon" className="ml-4" />
            </button>
          </div>
        </div>

        {items.map((item, index) => (
          <div
            className="flex flex-col md:flex-row col-span-full lg:col-start-4 lg:grid lg:grid-cols-3"
            // eslint-disable-next-line react/no-array-index-key -- this is correct key
            key={index}
          >
            {item.map(({ title, links }) => (
              <div
                key={title}
                className="p-4 md:w-64 md:mr-8 lg:w-full lg:col-span-1"
              >
                <p
                  className={clsx(
                    "mb-4 caption3 xl:caption2",
                    themeColorClassTwo,
                  )}
                >
                  {title}
                </p>
                <ul className="block" key={title}>
                  {links.map((link) => (
                    <FooterLink
                      key={link.text}
                      clickhandler={link.clickhandler}
                      target={link.target}
                      to={link.to}
                      text={link.text}
                      outbound={link.outbound}
                      className={clsx(
                        "pb-3 body3 footer-link",
                        themeColorClassOne,
                      )}
                    />
                  ))}
                </ul>
              </div>
            ))}
          </div>
        ))}
      </PageGrid>
      <PageGrid className="mt-16 lg:mt-22">
        <div className="flex flex-col justify-start col-span-full md:flex-row md:justify-between md:items-center">
          <div className="flex p-4">
            <Link
              href="https://www.facebook.com/rippleofficial"
              aria-label="Facebook link for Ripple"
            >
              <FBIcon
                className={clsx("mr-3", themeColorClassThree)}
                alt="Facebook link for Ripple"
              />
            </Link>
            <Link
              className="pt-[10px]"
              href="https://x.com/Ripple"
              aria-label="Twitter link for Ripple Official Handle"
            >
              <TwitterIcon
                className={clsx("mr-3", themeColorClassThree)}
                alt="Twitter link for Ripple Official Handle"
              />
            </Link>
            <Link
              href="https://www.youtube.com/channel/UCjok1uTSBUgvRYQaASz6YWw"
              aria-label="YouTube link for Ripple Channel"
            >
              <YoutubeIcon
                className={clsx("mr-2", themeColorClassThree)}
                alt="YouTube link for Ripple Channel"
              />
            </Link>
            <Link
              href="https://www.linkedin.com/company/rippleofficial"
              aria-label="Ripple’s LinkedIn link"
            >
              <LinkedInIcon
                className={clsx(themeColorClassThree)}
                alt="Ripple’s LinkedIn link"
              />
            </Link>
          </div>
          <div className="p-4">
            <div className="flex items-center">
              <Link
                href="/legal/terms-of-use"
                className={clsx("text-xs xl:body4", themeColorClassOne)}
              >
                Terms
              </Link>
              <span
                className={clsx("mx-2 text-xs xl:body4", themeColorClassTwo)}
              >
                {" "}
                |{" "}
              </span>
              <Link
                href="/legal/privacy-policy"
                className={clsx("text-xs xl:body4", themeColorClassOne)}
              >
                Privacy
              </Link>
            </div>
            <p className={clsx("mt-3 text-xs xl:body4", themeColorClassTwo)}>
              © 2013 - {new Date().getFullYear()} Ripple, All Rights Reserved.
              <br />
              NMLS #2314015
            </p>
          </div>
        </div>
      </PageGrid>
    </footer>
  )
}

export default Footer
